import React, { createContext, useContext, useEffect, useState } from "react";
import { 
  User as FirebaseUser, 
  onAuthStateChanged, 
  signOut as firebaseSignOut 
} from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { doc, onSnapshot, Timestamp, updateDoc } from "firebase/firestore";
import { useUserStore } from "../store/user.store";
import { UserRole, User } from "../types/index";
import { toast } from "react-hot-toast";

// Use User type alias for clarity
export type UserProfile = User;

export interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  setCurrentRole: (role: UserRole) => void;
}

// Create context with default values matching the interface
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  setCurrentRole: () => {},
});

// Explicitly type the hook's return value
export const useAuth = (): AuthContextType => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, setUserProfile, clearUserProfile } = useUserStore();
  
  // Initialize from persisted store to prevent flash + auth state
  const [user, setUser] = useState<UserProfile | null>(userProfile || null);
  const [loading, setLoading] = useState(true);

  // Debug logging
  useEffect(() => {
    if (user) {
      console.log("🔐 Auth State:", {
        uid: user.uid,
        roles: user.roles,
        currentRole: user.currentRole,
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDocRef = doc(db, "users", firebaseUser.uid);
        
        // Real-time listener for user profile changes (both branches)
        const unsubscribeSnapshot = onSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const userData = docSnap.data();
              
              // Complete user profile merging Firebase Auth + Firestore data
              const fullUser: UserProfile = {
                uid: firebaseUser.uid,
                email: firebaseUser.email || "",
                photoURL: firebaseUser.photoURL || "",
                name: userData.name || firebaseUser.displayName || "",
                phone: userData.phone || firebaseUser.phoneNumber || "",
                address: userData.address || "",
                preferences: userData.preferences || {},
                roles: (userData.roles as UserRole[]) || ["customer"],
                currentRole: (userData.currentRole as UserRole) || "customer",
                bio: userData.bio || "",
                cuisines: userData.cuisines || [],
                createdAt: userData.createdAt,
                communicationChannels: userData.communicationChannels || [],
                walletBalance: (userData.walletBalance as number) || 0,
                // Spread any additional custom fields
                ...userData
              } as UserProfile;

              setUser(fullUser);
              setUserProfile(fullUser);
            } else {
              // New user fallback - create basic profile
              const basicUser: UserProfile = {
                uid: firebaseUser.uid,
                name: firebaseUser.displayName || "",
                email: firebaseUser.email || "",
                phone: firebaseUser.phoneNumber || "",
                photoURL: firebaseUser.photoURL || "",
                address: "",
                roles: ["customer"],
                currentRole: "customer",
                createdAt: Timestamp.now(),
                preferences: {},
                cuisines: [],
                communicationChannels: [],
                walletBalance: 0
              } as UserProfile;
              
              setUser(basicUser);
              setUserProfile(basicUser);
            }
            setLoading(false);
          },
          (error) => {
            console.error("🔥 Auth Firestore Sync Error:", error);
            toast.error("Profile sync failed");
            setLoading(false);
          }
        );

        return () => unsubscribeSnapshot();
      } else {
        // User logged out
        console.log("👋 User logged out");
        setUser(null);
        clearUserProfile();
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [setUserProfile, clearUserProfile]);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      clearUserProfile();
      toast.success("Logged out successfully 👋");
      window.location.href = "/login"; // Hard redirect for clean state
    } catch (error) {
      console.error("❌ Logout Error:", error);
      toast.error("Logout failed - please try again");
    }
  };

  const setCurrentRole = async (role: UserRole) => {
    if (!user) {
      toast.error("No user session");
      return;
    }

    // Optimistic UI update
    const updatedUser = { ...user, currentRole: role } as UserProfile;
    setUser(updatedUser);
    setUserProfile(updatedUser);

    try {
      // Persist to Firestore (real-time sync)
      await updateDoc(doc(db, "users", user.uid), { 
        currentRole: role 
      });
      toast.success(`Switched to ${role} role`);
      console.log(`✅ Role switched to: ${role}`);
    } catch (error) {
      console.error("❌ Failed to save role change:", error);
      toast.error("Failed to switch role - retrying...");
      // Revert optimistic update on failure
      setUserProfile(user);
      setUser(user);
    }
  };

  // Show loading spinner during auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut, setCurrentRole }}>
      {children}
    </AuthContext.Provider>
  );
};
